// Pure helpers for the pre-release launch benchmark: cumulative launch curves
// and at-the-same-age comparisons against normal releases and other
// pre-releases.

// Below this many games the chart's p25–p75 spread band means nothing.
// Averages are shown for any group size; the group size is shown with them.
export const MIN_BAND_GAMES = 3;

export const HORIZONS = [7, 14, 30];

export const BENCH_METRICS = [
  { key: 'players',             label: 'Unique players' },
  { key: 'n_bets',              label: 'Bets' },
  { key: 'turnover_eur',        label: 'Turnover' },
  { key: 'expected_ggr_eur',    label: 'Expected GGR' },
  { key: 'ggr_eur',             label: 'Actual GGR' },
];

// Pre-releases, with or without a VIP phase. Everything else that's tracked
// ("standard") is a normal release.
const PRE_RELEASE_TYPES = new Set(['pre_release_vip', 'pre_release']);

export function isPreRelease(type) {
  return PRE_RELEASE_TYPES.has(type);
}


// One cumulative point per complete day since release (day 1 = the first 24
// hours). Days without activity carry the previous totals forward; points
// from day days_observed on are partial and left out.
export function cumulativeSeries(curve) {
  const byDay = new Map(curve.points.map(p => [p.day, p]));
  const series = [];
  let totals = { players: 0, n_bets: 0, turnover_eur: 0, ggr_eur: 0, expected_ggr_eur: 0 };
  for (let day = 0; day < curve.days_observed; day += 1) {
    const p = byDay.get(day);
    if (p) {
      totals = {
        players: totals.players + p.new_players,
        n_bets: totals.n_bets + p.n_bets,
        turnover_eur: totals.turnover_eur + p.turnover_eur,
        ggr_eur: totals.ggr_eur + p.ggr_eur,
        expected_ggr_eur: totals.expected_ggr_eur + p.expected_ggr_eur,
      };
    }
    series.push({ day: day + 1, ...totals });
  }
  return series;
}

// Cumulative value over the first `age` days, or null if the game isn't that old yet.
export function valueAt(series, metric, age) {
  if (age < 1 || series.length < age) return null;
  return series[age - 1][metric];
}

// Linear-interpolated quantile of an ascending array.
export function quantile(sorted, q) {
  if (sorted.length === 0) return null;
  const pos = (sorted.length - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
}

// The average of `metric` at `age` across the games old enough to have it,
// (null when none qualify), with p25/p75 for the chart's spread band (null
// below MIN_BAND_GAMES), plus the
// member games with their values, highest first, so a small group can be
// inspected.
export function benchmarkAtAge(pool, metric, age) {
  const members = pool
    .map(x => ({
      unique_game_id: x.curve.unique_game_id,
      game_title: x.curve.game_title,
      provider: x.curve.provider,
      category: x.curve.category,
      release_date: x.curve.release_date,
      value: valueAt(x.series, metric, age),
    }))
    .filter(m => m.value != null)
    .sort((a, b) => b.value - a.value);
  const values = members.map(m => m.value).sort((a, b) => a - b);
  const enough = values.length >= MIN_BAND_GAMES;
  return {
    n: values.length,
    p25: enough ? quantile(values, 0.25) : null,
    p75: enough ? quantile(values, 0.75) : null,
    mean: values.length > 0 ? values.reduce((s, v) => s + v, 0) / values.length : null,
    members,
  };
}

function ratio(value, base) {
  return value == null || base == null || base === 0 ? null : value / base;
}

function withSeries(curves) {
  return curves.map(curve => ({ curve, series: cumulativeSeries(curve) }));
}

// The benchmark group for `target` at `age`: the candidates other than the
// target that have a value at that age and had at least one player by then
// — optionally only its category's (e.g. slots vs slots), since tiny live
// tables and niche games would otherwise dwarf the averages
// — a catalog entry nobody played was most likely never live on the site,
// and counting it as 0 would drag the average down — optionally only its
// provider's —
// and, with lastN, only the N most recently released of those, whether
// released before or after the target. A release too young for the age is
// skipped, so the next most recent one takes its place.
export function benchmarkPool(candidates, target, age, metric, { sameProvider = false, sameCategory = false, lastN = null } = {}) {
  const eligible = candidates.filter(x => x.curve.unique_game_id !== target.unique_game_id
    && (!sameProvider || x.curve.provider === target.provider)
    && (!sameCategory || x.curve.category === target.category)
    && valueAt(x.series, metric, age) != null
    && valueAt(x.series, 'players', age) > 0);
  if (!lastN) return eligible;
  const releasedAt = x => new Date(x.curve.release_date);
  return [...eligible].sort((a, b) => releasedAt(b) - releasedAt(a)).slice(0, lastN);
}

// One row per pre-release, newest first as given: its value at age =
// min(days observed, horizon), the multiple of the normal releases' and of
// the other pre-releases' average at
// that same age, and its rank among those pre-releases. `sameProvider`, `sameCategory` and `lastN` narrow both benchmark groups (see
// benchmarkPool).
export function scorecardRows(curves, {
  metric, horizon, sameProvider = false, sameCategory = false, lastN = null,
}) {
  const all = withSeries(curves);
  const standard = all.filter(x => x.curve.release_type === 'standard');
  const preReleases = all.filter(x => isPreRelease(x.curve.release_type));
  const scope = { sameProvider, sameCategory, lastN };

  return preReleases.map(({ curve, series }) => {
    const age = Math.min(curve.days_observed, horizon);
    const value = valueAt(series, metric, age);
    const normal = benchmarkAtAge(benchmarkPool(standard, curve, age, metric, scope), metric, age);
    const peerPool = benchmarkPool(
      preReleases, curve, age, metric, scope,
    );
    const peers = benchmarkAtAge(peerPool, metric, age);
    const peerValues = peerPool.map(x => valueAt(x.series, metric, age));
    const rank = value == null ? null : { position: 1 + peerValues.filter(v => v > value).length, of: peerValues.length + 1 };
    return {
      curve,
      age,
      value,
      normal,
      peers,
      vsNormal: ratio(value, normal.mean),
      vsPeers: ratio(value, peers.mean),
      rank,
    };
  });
}

// Chart data for one game over days 1..horizon: the normal-release p25–p75
// band and its average, its peer pre-releases' average, and the game's own
// cumulative value. Unlike the scorecard, each line follows one fixed group
// on every day — the releases (up to lastN, most recent first) that have
// completed the whole horizon — so a line only changes because its games'
// totals grow, never because a newer game drops in or out of the group.
export function bandSeries(curves, selected, {
  metric, horizon, sameProvider = false, sameCategory = false, lastN = null,
}) {
  const all = withSeries(curves);
  const standard = all.filter(x => x.curve.release_type === 'standard');
  const peers = all.filter(x => isPreRelease(x.curve.release_type));
  const own = cumulativeSeries(selected);
  const scope = { sameProvider, sameCategory, lastN };
  const normalGroup = benchmarkPool(standard, selected, horizon, metric, scope);
  const peerGroup = benchmarkPool(peers, selected, horizon, metric, scope);

  return Array.from({ length: horizon }, (_, i) => {
    const age = i + 1;
    const normal = benchmarkAtAge(normalGroup, metric, age);
    return {
      day: age,
      band: normal.p25 == null ? null : [normal.p25, normal.p75],
      normal_avg: normal.mean,
      normal_n: normal.n,
      pre_avg: benchmarkAtAge(peerGroup, metric, age).mean,
      game: valueAt(own, metric, age),
    };
  });
}

// Days the checkpoint table reports totals at.
export const CHECKPOINT_DAYS = [7, 14, 30];

// One row per metric for one game: at each checkpoint day, its cumulative
// value since release and the normal-release and peer pre-release averages
// at that same day (groups picked as in scorecardRows), with the multiples.
// A day the game hasn't fully reached yet has a null value.
export function checkpointRows(curves, selected, {
  checkpoints = CHECKPOINT_DAYS, sameProvider = false, sameCategory = false, lastN = null,
} = {}) {
  const all = withSeries(curves);
  const standard = all.filter(x => x.curve.release_type === 'standard');
  const peers = all.filter(x => isPreRelease(x.curve.release_type));
  const own = cumulativeSeries(selected);
  const scope = { sameProvider, sameCategory, lastN };

  return BENCH_METRICS.map(({ key }) => ({
    metric: key,
    cells: checkpoints.map(day => {
      const value = valueAt(own, key, day);
      const normal = benchmarkAtAge(benchmarkPool(standard, selected, day, key, scope), key, day);
      const peerGroup = benchmarkAtAge(benchmarkPool(peers, selected, day, key, scope), key, day);
      return {
        day,
        value,
        normal,
        peers: peerGroup,
        vsNormal: ratio(value, normal.mean),
        vsPeers: ratio(value, peerGroup.mean),
      };
    }),
  }));
}

// Scorecard sort accessors by column key; text columns compare as strings
// (ISO dates sort correctly as strings), the rest as numbers.
const SCORECARD_SORT = {
  game_title:   { text: true, get: r => r.curve.game_title },
  provider:     { text: true, get: r => r.curve.provider },
  release_type: { text: true, get: r => r.curve.release_type },
  release_date: { text: true, get: r => r.curve.release_date },
  age:          { get: r => r.age },
  value:        { get: r => r.value },
  vsNormal:     { get: r => r.vsNormal },
  vsPeers:      { get: r => r.vsPeers },
  rank:         { get: r => r.rank?.position ?? null },
};

// A sorted copy of rows by one accessor; empty values always sink to the end.
function sortBy(rows, { text, get }, dir) {
  const sign = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = get(a);
    const bv = get(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return sign * (text ? String(av).localeCompare(String(bv)) : av - bv);
  });
}

// Line colours for the per-provider chart (identity, not magnitude), in
// order: the Game Performance palette, then five more distinct hues.
export const PROVIDER_LINE_COLORS = [
  '#3861FB', '#FFD22F', '#00BD71', '#E21351', '#d4a843',
  '#22d3ee', '#f97316', '#ec4899', '#a3e635', '#a78bfa',
];

const byNewest = (a, b) => new Date(b.release_date) - new Date(a.release_date);

// The By-provider view's "every provider" option.
export const ALL_PROVIDERS = '__all__';

function providerPreReleases(curves, provider) {
  return curves
    .filter(c => isPreRelease(c.release_type) && (provider === ALL_PROVIDERS || c.provider === provider))
    .sort(byNewest);
}

// Providers with at least one pre-release, most pre-releases first.
export function providerPreReleaseOptions(curves) {
  const counts = new Map();
  curves.filter(c => isPreRelease(c.release_type)).forEach(c => counts.set(c.provider, (counts.get(c.provider) || 0) + 1));
  return [...counts.entries()]
    .map(([provider, count]) => ({ provider, count }))
    .sort((a, b) => b.count - a.count || a.provider.localeCompare(b.provider));
}

// One chart line per given game (in the given order, at most one per
// colour): each game's own cumulative value by day since release, null past
// the days it has completed.
export function lineSeries(curves, metric, horizon) {
  const games = curves
    .slice(0, PROVIDER_LINE_COLORS.length)
    .map((curve, i) => ({ curve, key: `g${curve.unique_game_id}`, color: PROVIDER_LINE_COLORS[i], series: cumulativeSeries(curve) }));
  const data = Array.from({ length: horizon }, (_, i) => ({
    day: i + 1,
    ...Object.fromEntries(games.map(g => [g.key, valueAt(g.series, metric, i + 1)])),
  }));
  return { games, data };
}

// Each of the provider's pre-releases — or every provider's, with
// ALL_PROVIDERS — newest first, with its cumulative value at every
// checkpoint day (null where not reached yet).
export function providerCheckpointRows(curves, provider, metric, checkpoints = CHECKPOINT_DAYS) {
  return providerPreReleases(curves, provider).map(curve => {
    const series = cumulativeSeries(curve);
    return { curve, values: checkpoints.map(d => valueAt(series, metric, d)) };
  });
}

// A sorted copy of the scorecard rows; empty values always sink to the end.
export function sortScorecard(rows, col, dir) {
  return sortBy(rows, SCORECARD_SORT[col], dir);
}

// A sorted copy of the By-provider rows. Columns: game_title, release_type,
// release_date, or d<i> for the i-th checkpoint value; games that haven't
// reached a checkpoint yet always sink to the end.
export function sortProviderRows(rows, col, dir) {
  const checkpoint = /^d(\d+)$/.exec(col);
  const accessor = checkpoint
    ? { get: r => r.values[Number(checkpoint[1])] }
    : SCORECARD_SORT[col];
  return sortBy(rows, accessor, dir);
}
